import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import { Employee, SortField, SortOrder } from '../../types';

interface EmployeeTableProps {
  employees: Employee[];
  selectedEmployees: string[];
  searchQuery: string;
  sortField: SortField;
  sortOrder: SortOrder;
  loading?: boolean;
  onEmployeeSelect: (employeeId: string) => void;
  onSearchChange: (query: string) => void;
  onSort: (field: SortField) => void;
  onSaveListClick: () => void;
  onSelectAllEmployees: (checked: boolean) => void;
}

export const EmployeeTable: React.FC<EmployeeTableProps> = ({
  employees,
  selectedEmployees,
  searchQuery,
  sortField,
  sortOrder,
  loading = false,
  onEmployeeSelect,
  onSearchChange,
  onSort,
  onSaveListClick,
  onSelectAllEmployees,
}) => {
  const [selectionFilter, setSelectionFilter] = React.useState('all');

  const filteredAndSortedEmployees = employees
    .filter(emp => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        emp.name.toLowerCase().includes(searchLower) ||
        (emp.department || '').toLowerCase().includes(searchLower) ||
        emp.phone.toLowerCase().includes(searchLower)
      );

      switch (selectionFilter) {
        case 'selected':
          return matchesSearch && selectedEmployees.includes(emp.id);
        case 'unselected':
          return matchesSearch && !selectedEmployees.includes(emp.id);
        default:
          return matchesSearch;
      }
    })
    .sort((a, b) => {
      const aValue = (a[sortField] || '').toLowerCase();
      const bValue = (b[sortField] || '').toLowerCase();
      const compareResult = aValue.localeCompare(bValue);
      return sortOrder === 'asc' ? compareResult : -compareResult;
    });

  return (
    <Paper elevation={3} sx={{ height: '56vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            Employees ({loading ? '...' : filteredAndSortedEmployees.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={onSaveListClick}
            size="small"
            disabled={loading}
          >
            Save List
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: 1 }}
            size="small"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Selection Filter</InputLabel>
            <Select
              value={selectionFilter}
              onChange={(e) => setSelectionFilter(e.target.value)}
              label="Selection Filter"
              disabled={loading}
            >
              <MenuItem value="all">All Employees</MenuItem>
              <MenuItem value="selected">Selected Only</MenuItem>
              <MenuItem value="unselected">Unselected Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <TableContainer sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Loading employee data...
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employees.length}
                    checked={selectedEmployees.length === employees.length}
                    onChange={(e) => onSelectAllEmployees(e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortOrder : 'asc'}
                    onClick={() => onSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'department'}
                    direction={sortField === 'department' ? sortOrder : 'asc'}
                    onClick={() => onSort('department')}
                  >
                    Department
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'phone'}
                    direction={sortField === 'phone' ? sortOrder : 'asc'}
                    onClick={() => onSort('phone')}
                  >
                    Phone
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedEmployees.map((employee) => (
                <TableRow
                  key={employee.id}
                  hover
                  onClick={() => onEmployeeSelect(employee.id)}
                  selected={selectedEmployees.includes(employee.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedEmployees.includes(employee.id)}
                    />
                  </TableCell>
                  <TableCell>{employee.name}</TableCell>
                  <TableCell>{employee.department}</TableCell>
                  <TableCell sx={{ 
                    color: employee.isEmergencyNumber ? 'inherit' : 'error.main',
                    position: 'relative',
                    '& .emergency-tooltip': {
                      visibility: 'hidden',
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: '#fff',
                      p: '4px 8px',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      boxShadow: 1,
                      fontSize: '0.75rem',
                      zIndex: 1,
                      mb: 0.5,
                      width: 'max-content',
                      maxWidth: '200px',
                      textAlign: 'center',
                      lineHeight: '1.2',
                    },
                    '&:hover .emergency-tooltip': {
                      visibility: 'visible',
                    },
                  }}>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      {employee.phone}
                      {!employee.isEmergencyNumber && (
                        <Box className="emergency-tooltip">
                          No Emergency # on the record!<br />Using Mobile Phone #
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Paper>
  );
};

export default EmployeeTable;
